import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { TokenRefreshService } from '../services/token-refresh.service';

function isAuthPath(url: string): boolean {
  const u = url.split('?')[0].toLowerCase();
  return [
    '/.well-known/openid-configuration',
    '/connect/authorize',
    '/connect/token',
    '/token',
    '/connect/logout',
    '/connect/userinfo',
    '/connect/revocation',
    '/connect/introspect',
    '/auth/login',
    '/auth/refresh',
    '/auth/logout',
    '/Auth/logout',
  ].some((p) => u.includes(p));
}

/**
 * Refresh Token Interceptor — reactive 401 handler.
 *
 * On 401:
 *  1. Delegates to TokenRefreshService.refresh$() which serialises concurrent
 *     requests so only one refresh HTTP call is ever made at a time.
 *  2. Retries the original request with the new access token.
 *  3. If refresh fails, TokenRefreshService.logout() clears storage and
 *     publishes isUnauthorized$ → AppComponent navigates to /login.
 *
 * Proactive refresh (before expiry) is handled by TokenRefreshService timer,
 * so most requests never hit a 401 at all.
 */
export const refreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenRefresh = inject(TokenRefreshService);

  if (isAuthPath(req.url)) return next(req);

  return next(req).pipe(
    catchError((err) => {
      if (!(err instanceof HttpErrorResponse && err.status === 401)) {
        return throwError(() => err);
      }

      return tokenRefresh.refresh$().pipe(
        switchMap((newAccess) =>
          next(
            req.clone({ setHeaders: { Authorization: `Bearer ${newAccess}` } }),
          ),
        ),
        // If refresh itself failed, propagate the original 401 error
        catchError(() => throwError(() => err)),
      );
    }),
  );
};
